import axios from 'axios'
import config from '../../config'
import { extractPathFromWordpressUrl } from './helpers'

/**
 * WordPress class to interface with the wordpress backend in real-time
 *
 * The wordpress backend data in JSON format can be accessed by going to https://wordpress.dfs.team/wp-json
 * @author Samuel Gamage
 * @version 1.2
 */
class WP {
  // url to fetch wordpress data from
  url = config.wordpressUrl

  // token received from wordpress from being authenticated
  nonce = ''

  PER_PAGE = 100

  /**
   * @param {String} url wordpress url
   * @param {String} nonce wordpress token
   */
  constructor(nonce) {
    if (nonce) {
      this.nonce = nonce
    }
  }

  /**
   * Gets the wordpress posts
   * @return posts
   */
  posts() {
    return axios.get(`${this.url}/wp-json/wp/v2/posts`).then(res => res.data)
  }

  /**
   * Gets the wordpress post with matching id
   * @param {Number | String} id post id
   * @return post
   */
  async postWithId(id) {
    const res = await fetch(
      `${this.url}/wp-json/wp/v2/posts/${id}?_wpnonce=${this.nonce}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    )
    const postData = await res.json()

    const [
      featuredMediaData,
      authorData,
      categoriesData,
      tagsData,
    ] = await Promise.all([
      this.mediaWithId(postData.featured_media),
      this.userWithId(postData.author),
      this.getAllCategories(postData.categories),
      this.manyTags(postData.tags),
    ])

    if (featuredMediaData) {
      const mediaMetaData = featuredMediaData.media_details

      postData.featured_media = {
        height: mediaMetaData.height,
        width: mediaMetaData.width,
        src: featuredMediaData.source_url,
        alt: featuredMediaData.alt_text,
        id: featuredMediaData.id,
      }
    }

    postData.author = {
      name: authorData.name,
      slug: authorData.slug,
      path: extractPathFromWordpressUrl(authorData.link),
    }

    postData.categories = categoriesData
    postData.tags = tagsData

    return postData
  }

  /**
   * Get the pages
   * @return {Promise<Array>} pages
   */
  pages() {
    return axios
      .get(`${this.url}/wp-json/wp/v2/pages?per_page=${this.PER_PAGE}`)
      .then(res => res.data)
  }

  /**
   * Get the page with matching id
   * @param {Number | String} id page id
   * @return page
   */
  async pageWithId(id) {
    const res = await fetch(
      `${this.url}/wp-json/wp/v2/pages/${id}?_wpnonce=${this.nonce}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    )
    const pageData = await res.json()

    // fetch page resolver data asynchronously in parallel
    const [featuredMediaData, authorData, pagesData] = await Promise.all([
      this.mediaWithId(pageData.featured_media),
      this.userWithId(pageData.author),
      this.pages(),
    ])

    let childPages
    if (pagesData && Array.isArray(pagesData)) {
      childPages = pagesData.filter(page => page.parent === pageData.id)
    }

    if (featuredMediaData) {
      const metaData = featuredMediaData.media_details.sizes['full']
      pageData.featured_media = {
        height: (metaData && metaData.height) || 0,
        width: (metaData && metaData.width) || 0,
        src: (metaData && metaData.source_url) || '',
        alt: (metaData && metaData.alt_text) || '',
        id: (metaData && metaData.id) || '',
      }
    }

    if (authorData) {
      pageData.author = {
        name: authorData.name,
        slug: authorData.slug,
        path: extractPathFromWordpressUrl(authorData.link),
      }
    }

    return { ...pageData, children: childPages }
  }

  /**
   * Get all of the categories
   * @return categories
   */
  categories() {
    return axios
      .get(`${this.url}/wp-json/wp/v2/categories?per_page=${this.PER_PAGE}`)
      .then(res => res.data)
  }

  /**
   * Get all categories specified in an array of ids
   * @param {Array<Number | String>} categoryIds
   */
  async getAllCategories(categoryIds) {
    const categoryData = []

    await categoryIds.forEach(async id => {
      const category = await this.categoryWithId(id)
      categoryData.push(category)
    })

    return categoryData
  }

  /**
   * Get the category with matching id
   * @param {Number | String} id category id
   * @return category
   */
  categoryWithId(id) {
    return axios
      .get(`${this.url}/wp-json/wp/v2/categories/${id}`)
      .then(res => res.data)
  }

  /**
   * Get all of the tags
   * @return tags
   */
  tags() {
    return axios
      .get(`${this.url}/wp-json/wp/v2/tags?per_page=${this.PER_PAGE}`)
      .then(res => res.data)
  }

  /**
   * Get all tags specified in an array of ids
   * @param {Array<Number | String>} tagIds
   */
  async manyTags(tagIds) {
    const tagData = []

    await tagIds.forEach(async id => {
      const tag = await this.tagWithId(id)
      tagData.push(tag)
    })

    return tagData
  }

  /**
   * Get the tag with matching id
   * @param {Number | String} id tag id
   * @return tag
   */
  tagWithId(id) {
    return axios
      .get(`${this.url}/wp-json/wp/v2/tags/${id}`)
      .then(res => res.data)
  }

  /**
   * Get all of the media
   * @return All media
   */
  media() {
    return axios
      .get(`${this.url}/wp-json/wp/v2/media?_wpnonce=${this.nonce}`, {
        withCredentials: true,
      })
      .then(res => res.data)
  }

  /**
   * Get media with matching id
   * @param {Number | String} id media id
   * @return media
   */
  mediaWithId(id) {
    if (id === 0 || !id) {
      return null
    }
    return axios
      .get(`${this.url}/wp-json/wp/v2/media/${id}?_wpnonce=${this.nonce}`, {
        withCredentials: true,
      })
      .then(res => res.data)
  }

  /**
   * Get the users
   * @return users
   */
  users() {
    return axios
      .get(`${this.url}/wp-json/wp/v2/users?per_page=${this.PER_PAGE}`)
      .then(res => res.data)
  }

  /**
   * Get the user with matching id
   * @param {Number | String} id user id
   * @return user
   */
  userWithId(id) {
    return axios
      .get(`${this.url}/wp-json/wp/v2/users/${id}`)
      .then(res => res.data)
  }

  /**
   * Get the options
   * @return options
   */
  options() {
    return axios.get(`${this.url}/wp-json/acf/v2/options`).then(res => res.data)
  }

  /**
   * Get the acf data associated with the post or page id
   * @param {Number | String} id page or post id
   */
  acfWithId(id) {
    if (id === 0 || !id) {
      return null
    }
    return axios.get(`${this.url}/wp-json/acf/v2/${id}`).then(res => res.data)
  }
}

export default WP
